/**
 * Contabilización masiva por carpeta (compartido por ambas vistas).
 *
 * Flujo: al abrir carga los documentos DIRECTOS de la carpeta destino
 * (sin subcarpetas), el usuario arrastra los archivos contables, el
 * componente sugiere el emparejamiento y muestra una tabla de revisión
 * donde se confirma, corrige o descarta cada fila antes de subir. La
 * subida es secuencial y reutiliza el thunk `accounting` existente
 * (mismo efecto que el tab Contabilización del drawer: doc
 * "Contabilizacion No - …" + campo `accounting` en files/{id} + audit).
 *
 * Reglas de negocio acordadas:
 *  - El emparejamiento va por nombre exacto (sin extensión,
 *    case/acentos-insensible) y, solo si eso no da nada, por número de
 *    factura — "FE-00009151.pdf" cae sobre "FVE9151.pdf". El match por
 *    número se asigna con etiqueta propia ("Emparejado por n.º") para
 *    que se distinga del exacto y se pueda revisar. Ver
 *    lib/accounting_match.js.
 *  - Documentos que ya tienen `accounting` quedan fuera del matching.
 *  - Conflictos (dos contables → mismo documento) y ambigüedades
 *    (nombres o números duplicados en la carpeta) nunca se auto-asignan.
 *  - Solo se suben las filas con documento destino asignado.
 *  - NO toca la marca manual `accounted` (tag verde): son flujos
 *    independientes.
 */

import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useFirebase } from 'react-redux-firebase';
import { Modal, Upload, Table, Select, Tag, Button, Alert, Progress, Typography } from 'antd';
import { InboxOutlined } from '@ant-design/icons';

import { accounting as accountingAction } from '../../actions/storage/upload';
import { retryWithBackoff, yieldToEventLoop, isPermanentError } from './bulk_helpers';
import { hasAccounting } from '../../lib/accounting_refs';
// El emparejamiento (nombre exacto y numero de factura) vive en
// lib/accounting_match.js: modulo puro, testeable sin cargar React/antd.
import { buildTargetIndex, findCandidates } from '../../lib/accounting_match';

// El thunk `accounting` ya reintenta el put de Storage por dentro. Cuando aun
// asi agota la ventana de reintentos del SDK, repetir todo el dispatch desde
// aqui solo multiplica el peor caso de la fila (y la cola es secuencial: una
// fila lenta congela las demas). Mejor marcar error y dejarla para el boton
// "Reintentar fallidos", que el usuario dispara cuando la red ya volvio.
const isFinalForAccounting = (err) =>
  isPermanentError(err)
  || err?.code === 'storage/retry-limit-exceeded'
  // El documento ya tiene contabilización (una sola por documento): repetir
  // da siempre la misma respuesta.
  || err?.code === 'accounting-exists';

// ─── Enumeración de documentos directos de la carpeta ───────────────
// Soporta los dos modelos de datos y los dos tipos de referencia que
// entregan las vistas (mismo criterio de detección que BulkUploader):
//  - CollectionReference (subcarpetas): docs-puntero con `reference`
//    (modelo clásico) y arrays files[] con fileId (modelo árbol).
//  - DocumentReference (raíz del cliente / nodo del árbol): files[] del doc.
// Entradas inline sin fileId no tienen doc propio en files/ y no son
// contabilizables (mismo criterio que el detalle de archivo).
export async function listAccountingTargets(targetFolderRef, firebase) {
  if (!targetFolderRef) return [];
  // useFirebase() entrega el objeto extendido; en thunks llega el factory.
  // Resolver ambos, igual que getFirestore de documents/folder_ref.js.
  const db = (typeof firebase === 'function' ? firebase() : firebase).firestore();
  const seen = new Map(); // 'files/{id}' -> DocumentReference (dedupe puntero+inline)

  const collect = (data) => {
    if (!data) return;
    if (data.reference && typeof data.reference.get === 'function') {
      seen.set(data.reference.path, data.reference);
    }
    const files = Array.isArray(data.files) ? data.files : [];
    for (const e of files) {
      if (e && e.fileId) seen.set(`files/${e.fileId}`, db.collection('files').doc(e.fileId));
    }
  };

  if (typeof targetFolderRef.doc === 'function') {
    const snap = await targetFolderRef.get();
    snap.docs.forEach((d) => collect(d.data()));
  } else {
    const snap = await targetFolderRef.get();
    collect(snap.exists ? snap.data() : null);
  }

  // Lecturas en tandas para no disparar cientos de gets simultáneos
  // (mismo patrón que listExistingFiles de bulk_tree.js).
  const refs = Array.from(seen.values());
  const out = [];
  const CHUNK = 20;
  for (let i = 0; i < refs.length; i += CHUNK) {
    const chunk = refs.slice(i, i + CHUNK);
    const snaps = await Promise.all(chunk.map((r) => r.get().catch(() => null)));
    for (const s of snaps) {
      if (s?.exists && s.data()?.name) {
        out.push({
          key: s.ref.path,
          name: s.data().name,
          ref: s.ref,
          hasAccounting: hasAccounting(s.data().accounting),
        });
      }
    }
    await yieldToEventLoop();
  }
  return out;
}

// El reject del thunk `accounting` entrega la ACTION redux
// {type: UPLOAD.ERROR, code, message}, no un Error — normalizamos para
// que isPermanentError y los mensajes de fila funcionen.
const normalizeThunkError = (e) => {
  if (e instanceof Error) return e;
  const err = new Error(e?.message || 'Error al subir el archivo.');
  if (e?.code) err.code = e.code;
  return err;
};

const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const STATUS_META = {
  matched:   { color: 'green',   label: 'Emparejado' },
  // Coincide el numero de factura pero no el nombre completo (FE-00009151
  // contra FVE9151). Se asigna, pero con etiqueta propia: es la senal de que
  // esta fila conviene revisar antes de subir.
  numeric:   { color: 'blue',    label: 'Emparejado por n.º' },
  manual:    { color: 'green',   label: 'Asignado manual' },
  conflict:  { color: 'orange',  label: 'Conflicto' },
  ambiguous: { color: 'orange',  label: 'Ambiguo' },
  already:   { color: 'default', label: 'Ya tiene contabilización' },
  unmatched: { color: 'red',     label: 'Sin pareja' },
};

const EXEC_META = {
  uploading: { color: 'processing', label: 'Subiendo…' },
  done:      { color: 'success',    label: 'Subido' },
  error:     { color: 'error',      label: 'Error' },
};

let rowSeq = 0;

export function BulkAccounting({ open, onClose, targetFolderRef, targetFolderName, onComplete }) {
  const dispatch = useDispatch();
  const firebase = useFirebase();

  const [targets, setTargets] = useState([]);
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [targetsError, setTargetsError] = useState(null);
  const [rows, setRows] = useState([]);
  const [running, setRunning] = useState(false);
  const [finished, setFinished] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [globalError, setGlobalError] = useState(null);

  const cancelRef = useRef(false);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const anySuccessRef = useRef(false);

  // Índices de matching derivados de los targets frescos (por nombre y por
  // número de factura). Ver lib/accounting_match.js.
  const targetIndex = useMemo(() => buildTargetIndex(targets), [targets]);

  const targetsByKey = useMemo(() => new Map(targets.map((t) => [t.key, t])), [targets]);

  // Reset + carga de targets al abrir.
  useEffect(() => {
    if (!open) return;
    setTargets([]);
    setRows([]);
    setRunning(false);
    setFinished(false);
    setProgress({ done: 0, total: 0 });
    setGlobalError(null);
    setTargetsError(null);
    anySuccessRef.current = false;
    cancelRef.current = false;

    let alive = true;
    setLoadingTargets(true);
    listAccountingTargets(targetFolderRef, firebase)
      .then((list) => { if (alive) setTargets(list); })
      .catch((e) => {
        if (alive) setTargetsError(e?.message || 'No fue posible leer los documentos de la carpeta.');
      })
      .finally(() => { if (alive) setLoadingTargets(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, targetFolderRef]);

  const patchRow = useCallback((id, patch) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }, []);

  // ─── Ingreso de archivos + matching ───────────────────────────────
  const addFiles = useCallback((fileList) => {
    setRows((prev) => {
      const sigs = new Set(prev.map((r) => `${r.fileName}|${r.size}`));
      const taken = new Set(
        prev.filter((r) => !r.discarded && r.targetKey).map((r) => r.targetKey)
      );

      const fresh = [];
      for (const f of fileList) {
        const sig = `${f.name}|${f.size}`;
        if (sigs.has(sig)) continue; // duplicado exacto en la cola
        sigs.add(sig);
        fresh.push({
          id: `ba-${++rowSeq}`,
          file: f,
          fileName: f.name,
          size: f.size,
          status: 'unmatched',
          note: '',
          targetKey: null,
          discarded: false,
          execStatus: null,
          errorMsg: null,
          matchKind: null,   // 'exact' | 'numeric' — cómo se encontró el destino
          matchNumber: null, // número de factura que coincidió, si aplica
        });
      }

      // Primera pasada: candidatos por nombre exacto y, si no hay, por número.
      const provisional = new Map(); // targetKey -> rows[]
      for (const r of fresh) {
        const { kind, candidates, number } = findCandidates(r.fileName, targetIndex);
        if (kind === 'already') {
          r.status = 'already';
          r.note = 'El documento que coincide ya tiene contabilización; se excluyó del emparejamiento.';
        } else if (kind === 'none') {
          r.status = 'unmatched';
          r.note = 'Ningún documento de la carpeta coincide con este nombre.';
        } else if (candidates.length > 1) {
          // Nunca se auto-asigna con más de un candidato: es justo el caso
          // en que un soporte podría quedar pegado al documento equivocado.
          r.status = 'ambiguous';
          r.note = kind === 'numeric'
            ? `Hay varios documentos con el número ${number} en la carpeta; asigna el destino manualmente.`
            : 'Hay varios documentos con este nombre en la carpeta; asigna el destino manualmente.';
        } else if (taken.has(candidates[0].key)) {
          r.status = 'conflict';
          r.note = 'Otro archivo de la lista ya está asignado a ese documento.';
        } else {
          r.matchKind = kind;
          r.matchNumber = number;
          if (!provisional.has(candidates[0].key)) provisional.set(candidates[0].key, []);
          provisional.get(candidates[0].key).push(r);
        }
      }

      // Segunda pasada: dos o más contables hacia el mismo documento
      // dentro de esta tanda → conflicto, nadie se asigna.
      for (const [key, group] of provisional) {
        if (group.length === 1) {
          const r = group[0];
          r.targetKey = key;
          if (r.matchKind === 'numeric') {
            r.status = 'numeric';
            r.note = `Coincide el número ${r.matchNumber}, no el nombre completo. Revisa el destino.`;
          } else {
            r.status = 'matched';
            r.note = '';
          }
        } else {
          for (const r of group) {
            r.status = 'conflict';
            r.note = 'Varios archivos coinciden con el mismo documento; elige cuál asignar.';
          }
        }
      }

      return [...prev, ...fresh];
    });
  }, [targetIndex]);

  // ─── Asignación manual ────────────────────────────────────────────
  const assignTarget = (row, targetKey) => {
    if (!targetKey) {
      // Quitar asignación: vuelve a "sin pareja" para que quede claro
      // que la fila no se subirá tal como está.
      patchRow(row.id, { targetKey: null, status: 'unmatched', note: 'Sin documento destino asignado.' });
      return;
    }
    patchRow(row.id, {
      targetKey,
      // Reconfirmar el mismo destino no degrada la etiqueta (un 'Emparejado
      // por n.º' revisado y dejado igual sigue diciendo por qué se emparejó).
      status: row.targetKey === targetKey ? row.status : 'manual',
      note: '',
    });
  };

  // Filas que efectivamente se subirán.
  const activeRows = rows.filter((r) => !r.discarded && r.targetKey && r.execStatus !== 'done');
  const doneCount = rows.filter((r) => r.execStatus === 'done').length;
  const errorCount = rows.filter((r) => r.execStatus === 'error' && !r.discarded).length;
  const eligibleCount = targets.filter((t) => !t.hasAccounting).length;
  const accountedCount = targets.length - eligibleCount;

  // Opciones del Select por fila: elegibles no usados por otra fila activa.
  const optionsForRow = (row) => {
    const usedByOthers = new Set(
      rows
        .filter((r) => r.id !== row.id && !r.discarded && r.targetKey)
        .map((r) => r.targetKey)
    );
    const list = targets
      .filter((t) => !t.hasAccounting && !usedByOthers.has(t.key))
      .map((t) => ({ value: t.key, label: t.name }));

    // El destino de ESTA fila puede haber salido del pool de elegibles —
    // pasa siempre al subir bien, porque la subida lo marca hasAccounting.
    // Sin la opción, antd no encuentra el label y pinta el value crudo
    // ("files/Archivo No - 1789…") en vez del nombre del documento.
    const own = row.targetKey ? targetsByKey.get(row.targetKey) : null;
    if (own && !list.some((o) => o.value === own.key)) {
      list.unshift({ value: own.key, label: own.name });
    }
    return list;
  };

  // ─── Subida secuencial ────────────────────────────────────────────
  // Procesa toda fila activa (con destino y sin subir): incluye tanto
  // las nuevas como las que fallaron en una corrida anterior.
  const startUpload = async () => {
    const list = rowsRef.current.filter(
      (r) => !r.discarded && r.targetKey && r.execStatus !== 'done'
    );
    if (!list.length) return;

    // Defensa extra: un documento destino no puede repetirse entre filas.
    const seen = new Set();
    for (const r of list) {
      if (seen.has(r.targetKey)) {
        setGlobalError('Hay dos archivos asignados al mismo documento destino. Corrige la asignación antes de subir.');
        return;
      }
      seen.add(r.targetKey);
    }
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      setGlobalError('Sin conexión a internet. Verifica la red e intenta de nuevo.');
      return;
    }

    setGlobalError(null);
    setFinished(false);
    setRunning(true);
    cancelRef.current = false;
    setProgress({ done: 0, total: list.length });
    let processed = 0;

    for (const row of list) {
      if (cancelRef.current) {
        patchRow(row.id, { execStatus: null });
        continue;
      }
      patchRow(row.id, { execStatus: 'uploading', errorMsg: null });
      try {
        const target = targetsByKey.get(row.targetKey);
        if (!target) throw new Error('El documento destino ya no está disponible.');

        // Frescura: otro usuario pudo contabilizarlo durante la revisión.
        const freshSnap = await retryWithBackoff(() => target.ref.get(), {
          attempts: 3, baseDelay: 1000, maxDelay: 5000,
        });
        if (!freshSnap.exists) throw new Error('El documento destino ya no existe.');
        if (hasAccounting(freshSnap.data().accounting)) {
          throw new Error('Otro usuario ya subió una contabilización a este documento.');
        }

        await retryWithBackoff(
          () => dispatch(accountingAction(row.file, freshSnap)).catch((a) => {
            throw normalizeThunkError(a);
          }),
          {
            attempts: 3,
            baseDelay: 1500,
            maxDelay: 8000,
            isAbort: () => cancelRef.current,
            isPermanent: isFinalForAccounting,
          }
        );

        anySuccessRef.current = true;
        patchRow(row.id, { execStatus: 'done' });
        // El destino quedó contabilizado: sale del pool de elegibles.
        setTargets((prev) => prev.map((t) => (t.key === row.targetKey ? { ...t, hasAccounting: true } : t)));
      } catch (e) {
        const err = normalizeThunkError(e);
        const msg = err.message === '__ABORTED__'
          ? 'Cancelado por el usuario.'
          : err.message === '__OFFLINE__'
            ? 'Sin conexión a internet.'
            : err.code === 'storage/retry-limit-exceeded'
              // El SDK lo reporta en inglés ("Max retry time for operation
              // exceeded"); aquí lo lee personal contable.
              ? 'La red no aguantó la subida de este archivo. Usa "Reintentar fallidos" cuando la conexión esté estable.'
              : err.message || 'Error desconocido.';
        // permission-denied y similares no se reintentan (retryWithBackoff
        // los corta); dejamos que el loop continúe para que el resumen
        // muestre el estado real de cada fila.
        patchRow(row.id, { execStatus: 'error', errorMsg: msg });
      }
      processed += 1;
      setProgress({ done: processed, total: list.length });
      await yieldToEventLoop();
    }

    setRunning(false);
    setFinished(true);
    if (anySuccessRef.current && typeof onComplete === 'function') {
      try { onComplete(); } catch { /* el refresco es best-effort */ }
    }
  };

  const handleClose = () => {
    if (running) return; // hay que cancelar primero
    onClose?.();
  };

  // ─── Tabla de revisión ────────────────────────────────────────────
  const columns = [
    {
      title: 'Archivo contable',
      dataIndex: 'fileName',
      key: 'fileName',
      ellipsis: true,
      render: (name, row) => (
        <span style={{ opacity: row.discarded ? 0.45 : 1 }}>
          <Typography.Text delete={row.discarded}>{name}</Typography.Text>
          <Typography.Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
            {formatSize(row.size)}
          </Typography.Text>
        </span>
      ),
    },
    {
      title: 'Estado',
      key: 'status',
      width: 190,
      render: (_, row) => {
        if (row.discarded) return <Tag>Descartado</Tag>;
        if (row.execStatus && EXEC_META[row.execStatus]) {
          const m = EXEC_META[row.execStatus];
          return (
            <>
              <Tag color={m.color}>{m.label}</Tag>
              {row.execStatus === 'error' && row.errorMsg ? (
                <Typography.Text type="danger" style={{ fontSize: 12, display: 'block' }}>
                  {row.errorMsg}
                </Typography.Text>
              ) : null}
            </>
          );
        }
        const m = STATUS_META[row.status] || STATUS_META.unmatched;
        return (
          <>
            <Tag color={m.color}>{m.label}</Tag>
            {row.note ? (
              <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                {row.note}
              </Typography.Text>
            ) : null}
          </>
        );
      },
    },
    {
      title: 'Documento destino',
      key: 'target',
      width: 300,
      render: (_, row) => (
        // Fila ya subida: texto plano. Un desplegable deshabilitado no aporta
        // nada y era donde aparecía la ruta interna del documento.
        row.execStatus === 'done' ? (
          <Typography.Text>
            {targetsByKey.get(row.targetKey)?.name || '—'}
          </Typography.Text>
        ) : (
          <Select
            style={{ width: '100%' }}
            placeholder="Asignar documento…"
            value={row.targetKey || undefined}
            onChange={(v) => assignTarget(row, v || null)}
            options={optionsForRow(row)}
            showSearch
            allowClear
            optionFilterProp="label"
            disabled={row.discarded || running}
          />
        )
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 110,
      render: (_, row) =>
        row.execStatus === 'done' ? null : (
          <Button
            type="link"
            size="small"
            danger={!row.discarded}
            disabled={running}
            onClick={() => patchRow(row.id, { discarded: !row.discarded })}
          >
            {row.discarded ? 'Restaurar' : 'Descartar'}
          </Button>
        ),
    },
  ];

  const footer = [
    <Button key="close" onClick={handleClose} disabled={running}>
      {finished ? 'Cerrar' : 'Cancelar'}
    </Button>,
  ];
  if (running) {
    footer.push(
      <Button key="stop" danger onClick={() => { cancelRef.current = true; }}>
        Cancelar subida
      </Button>
    );
  } else {
    // Un solo botón de acción: sube las filas pendientes, que tras una
    // corrida con errores son exactamente los reintentos + lo nuevo.
    const isRetryOnly = finished && errorCount > 0 && activeRows.length === errorCount;
    footer.push(
      <Button
        key="run"
        type="primary"
        disabled={activeRows.length === 0 || loadingTargets}
        onClick={() => startUpload()}
      >
        {isRetryOnly
          ? `Reintentar fallidos (${errorCount})`
          : `Subir ${activeRows.length} contabilizaci${activeRows.length === 1 ? 'ón' : 'ones'}`}
      </Button>
    );
  }

  return (
    <Modal
      open={open}
      title={`Contabilización masiva${targetFolderName ? ` — ${targetFolderName}` : ''}`}
      width={980}
      onCancel={handleClose}
      closable={!running}
      maskClosable={false}
      destroyOnClose
      footer={footer}
    >
      {targetsError ? (
        <Alert type="error" showIcon message={targetsError} style={{ marginBottom: 12 }} />
      ) : (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Los emparejamientos se sugieren por nombre de archivo (sin la extensión)."
          description={
            loadingTargets
              ? 'Leyendo los documentos de la carpeta…'
              : `${eligibleCount} documento${eligibleCount === 1 ? '' : 's'} elegible${eligibleCount === 1 ? '' : 's'} en esta carpeta` +
                (accountedCount > 0 ? ` · ${accountedCount} ya con contabilización` : '') +
                '. Solo se consideran los archivos directos de la carpeta (las subcarpetas no participan).'
          }
        />
      )}

      {!loadingTargets && !targetsError && targets.length === 0 ? (
        <Alert
          type="warning"
          showIcon
          message="Esta carpeta no tiene documentos elegibles para contabilizar."
          style={{ marginBottom: 12 }}
        />
      ) : (
        <Upload.Dragger
          multiple
          fileList={[]}
          beforeUpload={(_file, batch) => {
            // antd invoca beforeUpload por archivo; procesamos la tanda
            // completa una sola vez (cuando llega el primero del batch).
            if (batch && batch[0] === _file) addFiles(batch);
            return false; // nunca subir vía antd: nosotros controlamos todo
          }}
          disabled={running || loadingTargets || eligibleCount === 0}
          style={{ marginBottom: 12 }}
        >
          <p className="ant-upload-drag-icon"><InboxOutlined /></p>
          <p className="ant-upload-text">Arrastra aquí los archivos de contabilización</p>
          <p className="ant-upload-hint">o haz clic para seleccionarlos. Se aceptan archivos de cualquier tipo.</p>
        </Upload.Dragger>
      )}

      {globalError ? (
        <Alert type="error" showIcon message={globalError} closable onClose={() => setGlobalError(null)} style={{ marginBottom: 12 }} />
      ) : null}

      {running || finished ? (
        <div style={{ marginBottom: 12 }}>
          <Progress
            percent={progress.total ? Math.round((progress.done / progress.total) * 100) : 0}
            status={running ? 'active' : errorCount > 0 ? 'exception' : 'success'}
          />
          {finished ? (
            <Typography.Text type={errorCount > 0 ? 'danger' : 'success'}>
              {doneCount} subida{doneCount === 1 ? '' : 's'} exitosa{doneCount === 1 ? '' : 's'}
              {errorCount > 0 ? ` · ${errorCount} con error` : ''}
            </Typography.Text>
          ) : null}
        </div>
      ) : null}

      {rows.length > 0 ? (
        <Table
          size="small"
          rowKey="id"
          columns={columns}
          dataSource={rows}
          pagination={rows.length > 8 ? { pageSize: 8, showSizeChanger: false } : false}
        />
      ) : null}
    </Modal>
  );
}
